import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, CheckCircle2 } from "lucide-react";

export default function DocumentSubmissionPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const nav = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get(`/documents/assignments/${id}`);
            setData(data);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    };
    useEffect(() => { load(); }, [id]);

    const ack = async () => {
        await api.post(`/documents/assignments/${id}/acknowledge`);
        nav("/documents");
    };

    if (err) return <div className="p-8 text-[var(--danger)]">{err}</div>;
    if (!data) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    const { assignment, template } = data;
    const submitted = assignment.status === "submitted";

    return (
        <div className="p-8 max-w-3xl mx-auto space-y-6" data-testid="submission-page">
            <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--gold)]"><ChevronLeft size={14} /> Documents</Link>
            <div>
                <p className="label-tiny mb-2">/ Submission</p>
                <h1 className="font-serif text-5xl mb-2">{template?.title}</h1>
                <p className="text-sm text-[var(--text-muted)]">{template?.description}</p>
            </div>

            {!submitted && <div className="border border-[var(--border-bright)] bg-[var(--surface)] p-5 text-sm text-[var(--text-muted)]">
                Not yet submitted by the client.
            </div>}

            {submitted && (
                <div className="space-y-5">
                    <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
                        <p className="label-tiny mb-3">/ Signed</p>
                        <p className="font-serif text-3xl italic">{assignment.signature_name}</p>
                        <p className="font-mono text-xs text-[var(--text-muted)] mt-2">Signed {new Date(assignment.signed_at).toLocaleString()}</p>
                    </div>

                    <div className="border border-[var(--border)] bg-[var(--surface)] divide-y divide-[var(--border)]">
                        {(template?.fields || []).map((f) => {
                            const v = assignment.submitted_data?.[f.id];
                            return (
                                <div key={f.id} className="p-4" data-testid={`sub-field-${f.id}`}>
                                    <p className="label-tiny mb-2">{f.label}{f.required ? " *" : ""}</p>
                                    <div className="text-sm">
                                        {f.type === "acknowledge"
                                            ? (v ? <span className="text-[var(--gold-bright)]">Acknowledged</span> : <span className="text-[var(--danger)]">Not acknowledged</span>)
                                            : <span className="whitespace-pre-wrap">{v || <span className="text-[var(--text-muted)]">—</span>}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {!assignment.acknowledged_by_coach && (
                        <button onClick={ack} className="btn-primary inline-flex items-center gap-2" data-testid="ack-btn">
                            <CheckCircle2 size={14} /> Mark as reviewed
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
